import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PersonaService } from '@/app/core/current-user/persona.service';

/**
 * The home route (`''`, where the sign-in callback lands): sends the actor to its persona's home (T1-07) — the operator
 * to the dashboard as before, a single-tenant administrator straight into its tenant hub, a multi-tenant administrator to
 * the portfolio, a user to its applications. A persona that could not be decided (`unresolved`: `Me` or the portfolio
 * read failed) stays here, on the notice with a retry — it is never sent to a page chosen on a guess.
 */
export const personaHomeGuard: CanActivateFn = async () => {
    // inject() only works synchronously, before the first await.
    const persona = inject(PersonaService);
    const router = inject(Router);
    await persona.ensureResolved();
    return persona.kind() === 'unresolved' ? true : router.parseUrl(persona.homeUrl());
};

/**
 * Route guard factory for a page that belongs to some personas only (the operator's dashboard, the administrator's
 * portfolio): anyone else is sent to its OWN home, never to /zabranjen-pristup — the page is not forbidden to it, it is
 * simply not its page (e.g. a single-tenant admin opening the old home bookmark lands in its hub). An `unresolved` persona
 * is sent to the home route's notice.
 * Usage: `canActivate: [requirePersonaPage((p) => p.isOperator())]`.
 */
export function requirePersonaPage(isOwnPage: (persona: PersonaService) => boolean): CanActivateFn {
    return async () => {
        const persona = inject(PersonaService);
        const router = inject(Router);
        await persona.ensureResolved();
        return isOwnPage(persona) ? true : router.parseUrl(persona.homeUrl());
    };
}
